import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { GlassCard } from "@/components/GlassCard";
import { StatusBadge } from "@/components/StatusBadge";
import { ArrowLeft } from "lucide-react";

export default function VersionHistory() {
  const { id } = useParams();
  const [sop, setSop] = useState(null);
  const [versions, setVersions] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const [s, v, u] = await Promise.all([
        api.get(`/sops/${id}`),
        api.get(`/sops/${id}/versions`),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setSop(s.data.sop);
      setVersions(v.data);
      setUsers(u.data);
      setSelected(v.data[0] || null);
    })();
  }, [id]);

  const userMap = Object.fromEntries(users.map((u) => [u.id, u]));

  return (
    <div className="space-y-6" data-testid="version-history-page">
      <button
        onClick={() => navigate(-1)}
        className="text-xs text-[#9AA4B2] hover:text-white flex items-center gap-1"
      >
        <ArrowLeft size={12} /> Back
      </button>

      <header>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-white">Version history</h1>
        {sop && <div className="text-[#9AA4B2] text-sm">{sop.title}</div>}
      </header>

      <div className="grid lg:grid-cols-3 gap-4">
        <GlassCard className="p-4 lg:col-span-1" data-testid="version-list">
          <div className="text-xs uppercase tracking-widest text-[#9AA4B2] mb-2">All versions</div>
          <ol className="space-y-1">
            {versions.map((v) => (
              <li key={v.id}>
                <button
                  onClick={() => setSelected(v)}
                  className={`w-full text-left px-3 py-2.5 rounded-2xl border transition ${
                    selected?.id === v.id
                      ? "bg-white/[0.06] border-white/20"
                      : "bg-transparent border-transparent hover:bg-white/[0.03]"
                  }`}
                  data-testid={`version-item-${v.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">v{v.version_number}</span>
                    <StatusBadge status={v.status} />
                  </div>
                  <div className="text-[11px] text-[#9AA4B2] mt-0.5">
                    {new Date(v.created_at).toLocaleDateString()} · {userMap[v.created_by]?.name || "—"}
                  </div>
                  {v.changelog && (
                    <div className="text-[11px] text-[#5B6472] mt-1 line-clamp-2">{v.changelog}</div>
                  )}
                </button>
              </li>
            ))}
          </ol>
        </GlassCard>

        <GlassCard className="p-6 lg:col-span-2" data-testid="version-detail">
          {selected ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-display text-2xl text-white">v{selected.version_number}</div>
                  <div className="text-xs text-[#9AA4B2]">
                    {new Date(selected.created_at).toLocaleString()} · by {userMap[selected.created_by]?.name || "—"}
                  </div>
                </div>
                <StatusBadge status={selected.status} />
              </div>
              {selected.changelog && (
                <GlassCard className="p-3 mb-4">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1">Changelog</div>
                  <div className="text-sm text-white/90">{selected.changelog}</div>
                </GlassCard>
              )}
              <div
                className="prose-sop"
                dangerouslySetInnerHTML={{ __html: selected.content || "<p class='text-[#5B6472]'>No content in this revision.</p>" }}
              />
            </>
          ) : (
            <div className="text-[#5B6472] text-sm">Select a version to preview its content and changelog.</div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
